import React, {useCallback} from 'react';
import useConstant from 'use-constant';
import {useAsync} from 'react-async';
import awesomeDebouncePromise from 'awesome-debounce-promise';
import useAutosuggest from './autosuggest-hook/use-autosuggest';

const DEBOUNCE_TIMEOUT = 250;

const ListElement = props => <div>{props.children}</div>;
const InputElement = props => <input {...props} />;
const itemElementDefault = item => <div>{item.n}</div>;

const onItemSelectionDefault = change => {};
const itemToStringDefault = item => item.n;
const massageDataDefault = items => items;

const Autosuggest = props => {
  const {
    initialInputValue = '',
    placeholder = '',
    onItemSelection = onItemSelectionDefault,
    itemToString = itemToStringDefault,
    itemElement = itemElementDefault,
    getUrl,
    rf,
    width,
    Input = InputElement,
    List = ListElement,
    massageData = massageDataDefault,
    onInputValueChange = () => {},
    resetOnInputBlur = true,
    renderWhenListingNotThere = <></>,
    renerInitailView = <></>,
    onShowInitialPage = false,
    cursor,
    setStopCursorUpdate,
    setCursorResult,
    onEnterPress
  } = props;

  const fetchVoyagerLocationSuggestions = useCallback(
    async ({inputValue, rf}, {signal}) => {
      const fetchVoyagerUrlForInput = getUrl(inputValue, rf);

      if (!inputValue) {
        return [];
      }
      const res = await fetch(fetchVoyagerUrlForInput, {signal});
      if (!res.ok) throw new Error(res.statusText);
      const ret = await res.json();
      if (ret.predictions && ret.predictions) {
        return ret.predictions;
      }
    },
    [getUrl],
  );

  const debouncedVoyagerFetchPromise = useConstant(() =>
    awesomeDebouncePromise(fetchVoyagerLocationSuggestions, DEBOUNCE_TIMEOUT),
  );

  const inputRef = React.useRef(null);

  const {
    propGetters: {getInputProps, getMenuProps, getItemProps, getRootProps, inputHandleBlur, getLabelProps},
    state: {isOpen, inputValue},
  } = useAutosuggest({
    itemToString,
    initialInputValue,
    onInputValueChange,
    onShowInitialPage,
    onSelectedItemChange(changes) {
      onItemSelection(changes.selectedItem);
    },
    resetOnInputBlur,
  });

  const {data: items = [], isPending = false, isRejected = false, error = null} = useAsync({
    promiseFn: debouncedVoyagerFetchPromise,
    watch: inputValue,
    inputValue,
    rf
  });

  const itemsArr = massageData(items);

  React.useEffect(() => {
    if (inputValue?.length && !itemsArr?.length) {
      setCursorResult(-1)
    }
  }, [itemsArr, inputValue])

  React.useEffect(() => {
    if (itemsArr?.length && cursor === itemsArr?.length - 1) {
      setStopCursorUpdate(true)
    }
  }, [itemsArr, cursor])

  React.useEffect(() => {
    if (onEnterPress) {
      if (inputValue?.length && itemsArr?.length) {
        const selectedItem = itemsArr[cursor]
        onItemSelection(selectedItem)
        setStopCursorUpdate(false)
        setCursorResult(-1)
      }
      inputHandleBlur({resetInput: true})
    }
  }, [onEnterPress])

  const GetListingCard = ({item, index}) => {
    const fieldRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
      if (fieldRef.current) {
        if (cursor === index) {
          fieldRef.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest"
          })
        }
      }
    }, [cursor]); 
    return (
      <div
        style={{background: cursor === index ? '#eef4fd' : 'initial'}}
        key={item._id || index}
        {...getItemProps({
          item,
          index,
        })}
        ref={fieldRef}
        >
         {itemElement(item)}
      </div>
    )
  }

  return (
    <div style={{width: width ? width : "auto"}} {...getRootProps()}>
      <Input
        autoFocus
        name="autosuggest"
        type="text"
        {...getInputProps({
          ref: inputRef,
          placeholder
        })}
      />
      <List>
        <div {...getMenuProps()}>
          {!itemsArr?.length ? renerInitailView : null}
          {(isOpen &&
            itemsArr.map((item, index) => {
              return <GetListingCard item={item} index={index} />
            })) ||
            renderWhenListingNotThere}
        </div>
      </List>
    </div>
  );
};

export default React.memo(Autosuggest);
